import React, { useId, useRef } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import {
  ISLAND_PATHS,
  MRT_LINES,
  MRT_STATIONS,
  MAP_VIEWBOX_WIDTH,
  MAP_VIEWBOX_HEIGHT,
  lonLatToXY,
} from '../assets/singaporeMapData';
import { LRT_PATHS, OFFICIAL_RAIL_STATIONS, WATERBODY_PATHS } from '../assets/singaporeHydroLrtData';
import { CURRENT_MRT_PATHS, CURRENT_MRT_ROUTES, CURRENT_MRT_STATIONS } from '../assets/singaporeCurrentRailData';

/** Rect (canvas px) that the 1000-unit-wide map viewBox is scaled into. */
export interface MapRect {
  x: number;
  y: number;
  w: number;
}

export function mapRectHeight(rect: MapRect): number {
  return (rect.w / MAP_VIEWBOX_WIDTH) * MAP_VIEWBOX_HEIGHT;
}

/** Project a lon/lat to canvas px coordinates for a given map rect. */
export function projectToCanvas(
  rect: MapRect,
  lon: number,
  lat: number,
): { x: number; y: number } {
  const { x, y } = lonLatToXY(lon, lat);
  const s = rect.w / MAP_VIEWBOX_WIDTH;
  return { x: rect.x + x * s, y: rect.y + y * s };
}

interface DeckMapProps {
  rect: MapRect;
  /** Island fill — the deck uses a light grey silhouette. */
  islandFill?: string;
  showStations?: boolean;
  /** Mute lines/stations (cover page uses a fainter map). */
  opacity?: number;
  /** Hide detached offshore land while retaining every mainland region. */
  mainlandOnly?: boolean;
}

// The source geometry is split into five mainland planning-region polygons,
// followed by separate offshore-island polygons. Keeping only these five
// retains the detailed coastline and reservoir cut-outs without Pulau Ubin,
// Pulau Tekong, Jurong Island, Sentosa or the Southern Islands.
const MAINLAND_PATH_INDEXES = new Set([3, 4, 12, 14, 18]);

type MapPoint = readonly [number, number];

// DTL3 Extension (DT35 Expo -> DT36 Xilin -> DT37/TE31 Sungei Bedok).
// The operational OSM relation currently ends at Expo, so retain this
// confirmed extension explicitly until it is included in that relation.
const expoDtl = lonLatToXY(103.961548211018, 1.33454977824819);
const xilinDtl = lonLatToXY(103.9649, 1.3289);
const sungeiBedokDtl = lonLatToXY(103.9572, 1.3204);
const bedokSouthTel = lonLatToXY(103.9494, 1.31675);
const bukitPanjangDtl = lonLatToXY(103.76155, 1.379);
const de1Dtl = lonLatToXY(103.7461, 1.3976);
const sungeiKadutDtl = lonLatToXY(103.7488, 1.4133);
const bricklandNsl = lonLatToXY(103.7494, 1.3685);
const harbourFrontCcl = lonLatToXY(103.8215, 1.2654);
const keppelCcl = lonLatToXY(103.8302, 1.2694);
const cantonmentCcl = lonLatToXY(103.8379, 1.2729);
const princeEdwardCcl = lonLatToXY(103.8472, 1.2732);
const marinaBayCcl = lonLatToXY(103.8546, 1.2763);
const gulCircleCrl = lonLatToXY(103.6605, 1.3196);
const cr22 = lonLatToXY(103.681, 1.3188);
const cr21 = lonLatToXY(103.7005, 1.3195);
const cr20 = lonLatToXY(103.719, 1.326);
// Match the western endpoint of the baked CRL planning alignment so CRL3
// joins it without introducing a visible gap.
const cr19 = { x: 272.7, y: 295.8 };
// Share the CRL planning endpoint so both future services use one interchange
// marker instead of two nearby station symbols.
const changiT5Tel = { x: 797.8, y: 310.1 };
const changiAirportTel = lonLatToXY(103.9884, 1.3575);
const tanahMerahTel = lonLatToXY(103.946, 1.3272);
const DTL3_EXTENSION_PATH = [
  `M${expoDtl.x.toFixed(1)} ${expoDtl.y.toFixed(1)}`,
  `C${(expoDtl.x + 2).toFixed(1)} ${(expoDtl.y + 3).toFixed(1)} ${(xilinDtl.x - 1).toFixed(1)} ${(xilinDtl.y - 3).toFixed(1)} ${xilinDtl.x.toFixed(1)} ${xilinDtl.y.toFixed(1)}`,
  `C${(xilinDtl.x - 1).toFixed(1)} ${(xilinDtl.y + 5).toFixed(1)} ${(sungeiBedokDtl.x + 3).toFixed(1)} ${(sungeiBedokDtl.y - 3).toFixed(1)} ${sungeiBedokDtl.x.toFixed(1)} ${sungeiBedokDtl.y.toFixed(1)}`,
].join('');
const TEL5_EXTENSION_PATH = [
  // Continue from the existing TEL terminus at Bayshore (TE29).
  'M701.9 325.2',
  `C${(bedokSouthTel.x - 8).toFixed(1)} ${(bedokSouthTel.y + 1).toFixed(1)} ${(bedokSouthTel.x - 3).toFixed(1)} ${bedokSouthTel.y.toFixed(1)} ${bedokSouthTel.x.toFixed(1)} ${bedokSouthTel.y.toFixed(1)}`,
  `C${(bedokSouthTel.x + 5).toFixed(1)} ${(bedokSouthTel.y - 1).toFixed(1)} ${(sungeiBedokDtl.x - 4).toFixed(1)} ${(sungeiBedokDtl.y + 3).toFixed(1)} ${sungeiBedokDtl.x.toFixed(1)} ${sungeiBedokDtl.y.toFixed(1)}`,
].join('');
const DTL2_EXTENSION_PATH = [
  `M${bukitPanjangDtl.x.toFixed(1)} ${bukitPanjangDtl.y.toFixed(1)}`,
  `C${(bukitPanjangDtl.x - 4).toFixed(1)} ${(bukitPanjangDtl.y - 8).toFixed(1)} ${(de1Dtl.x + 5).toFixed(1)} ${(de1Dtl.y + 7).toFixed(1)} ${de1Dtl.x.toFixed(1)} ${de1Dtl.y.toFixed(1)}`,
  `C${(de1Dtl.x - 1).toFixed(1)} ${(de1Dtl.y - 7).toFixed(1)} ${(sungeiKadutDtl.x - 1).toFixed(1)} ${(sungeiKadutDtl.y + 6).toFixed(1)} ${sungeiKadutDtl.x.toFixed(1)} ${sungeiKadutDtl.y.toFixed(1)}`,
].join('');
const CCL6_PATH = [harbourFrontCcl, keppelCcl, cantonmentCcl, princeEdwardCcl, marinaBayCcl]
  .map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join('');
const CRL3_PATH = [cr19, cr20, cr21, cr22, gulCircleCrl]
  .map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join('');
const TEL_CHANGI_EXTENSION_PATH = [
  `M${sungeiBedokDtl.x.toFixed(1)} ${sungeiBedokDtl.y.toFixed(1)}`,
  // Keep the announced Sungei Bedok–T5 connection direct and uncluttered.
  `L${changiT5Tel.x.toFixed(1)} ${changiT5Tel.y.toFixed(1)}`,
  `L${changiAirportTel.x.toFixed(1)} ${changiAirportTel.y.toFixed(1)}`,
  // Return through the existing airport/Expo corridor towards Tanah Merah.
  `C779 251 758 271 ${expoDtl.x.toFixed(1)} ${expoDtl.y.toFixed(1)}`,
  // Bow south of the EWL airport branch so the two coloured routes remain
  // individually readable before meeting again at Tanah Merah.
  `C733 307 716 316 ${tanahMerahTel.x.toFixed(1)} ${tanahMerahTel.y.toFixed(1)}`,
].join('');

/**
 * Build presentable planned-line branches from the legacy workplan station
 * coordinates. Stations are ordered along each branch, so every branch starts
 * and ends at a station instead of exposing construction/turnback tails.
 */
function plannedStationRoutes(lineId: 'jrl' | 'crl'): MapPoint[][] {
  const line = MRT_LINES.find((item) => item.id === lineId);
  if (!line) return [];
  const stations = MRT_STATIONS
    .filter((station) => station.c.toLowerCase() === line.color.toLowerCase())
    .map((station) => [station.x, station.y] as MapPoint);
  const branches: MapPoint[][] = [];
  const matches = Array.from(line.path.matchAll(/([ML])(\d+(?:\.\d+)?) (\d+(?:\.\d+)?)/g));
  let branch: MapPoint[] = [];
  for (const match of matches) {
    const point = [Number(match[2]), Number(match[3])] as MapPoint;
    if (match[1] === 'M') {
      if (branch.length) branches.push(branch);
      branch = [point];
    } else {
      branch.push(point);
    }
  }
  if (branch.length) branches.push(branch);

  return branches.flatMap((points) => {
    if (points.length < 2) return [];
    let cumulative = 0;
    const segments = points.slice(0, -1).map((from, index) => {
      const to = points[index + 1];
      const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
      const segment = { from, to, start: cumulative, length };
      cumulative += length;
      return segment;
    });
    const ordered = stations.flatMap((station) => {
      let closest: { distance: number; progress: number } | null = null;
      for (const segment of segments) {
        const dx = segment.to[0] - segment.from[0]; const dy = segment.to[1] - segment.from[1];
        const denominator = dx * dx + dy * dy;
        const t = denominator ? Math.min(1, Math.max(0, ((station[0] - segment.from[0]) * dx + (station[1] - segment.from[1]) * dy) / denominator)) : 0;
        const x = segment.from[0] + dx * t; const y = segment.from[1] + dy * t;
        const distance = Math.hypot(station[0] - x, station[1] - y);
        if (!closest || distance < closest.distance) closest = { distance, progress: segment.start + segment.length * t };
      }
      return closest && closest.distance <= 28 ? [{ station, progress: closest.progress }] : [];
    }).sort((a, b) => a.progress - b.progress);
    const unique = ordered.filter((item, index) => index === 0 || item.station !== ordered[index - 1].station);
    return unique.length >= 2 ? [unique.map((item) => item.station)] : [];
  });
}

const PLANNED_ROUTES: Record<string, MapPoint[][]> = {
  jrl: plannedStationRoutes('jrl'),
  crl: plannedStationRoutes('crl'),
};

const FUTURE_COLORS = new Set(
  MRT_LINES.filter((line) => line.id === 'jrl' || line.id === 'crl').map((line) => line.color.toLowerCase()),
);
const plannedStationKeys = new Set(
  Object.values(PLANNED_ROUTES).flat(2).map(([x, y]) => `${x}:${y}`),
);
const RAW_DISPLAY_STATIONS = [
  ...CURRENT_MRT_STATIONS.map((station) => ({ ...station, type: 'MRT', interchange: station.i === 1 })),
  { ...xilinDtl, c: '#005ec4', i: 0, type: 'MRT', interchange: false },
  { ...bedokSouthTel, c: '#9D5B25', i: 0, type: 'MRT', interchange: false },
  { ...sungeiBedokDtl, c: '#005ec4', i: 1, type: 'MRT', interchange: true },
  { ...de1Dtl, c: '#005ec4', i: 0, type: 'MRT', interchange: false },
  { ...sungeiKadutDtl, c: '#005ec4', i: 1, type: 'MRT', interchange: true },
  { ...bricklandNsl, c: '#d42e12', i: 0, type: 'MRT', interchange: false },
  { ...keppelCcl, c: '#fa9e0d', i: 0, type: 'MRT', interchange: false },
  { ...cantonmentCcl, c: '#fa9e0d', i: 0, type: 'MRT', interchange: false },
  { ...princeEdwardCcl, c: '#fa9e0d', i: 0, type: 'MRT', interchange: false },
  { ...cr20, c: '#97C616', i: 0, type: 'MRT', interchange: false },
  { ...cr21, c: '#97C616', i: 0, type: 'MRT', interchange: false },
  { ...cr22, c: '#97C616', i: 0, type: 'MRT', interchange: false },
  { ...gulCircleCrl, c: '#97C616', i: 1, type: 'MRT', interchange: true },
  { ...changiT5Tel, c: '#9D5B25', i: 1, type: 'MRT', interchange: true },
  ...MRT_STATIONS
    .filter((station) => {
      if (!FUTURE_COLORS.has(station.c.toLowerCase()) || !plannedStationKeys.has(`${station.x}:${station.y}`)) return false;
      // The older CRL planning layer places T5 slightly north of the newer
      // shared CR1/TE32 position. Suppress that obsolete point; Aviation Park
      // is much farther north and remains visible.
      const isObsoleteT5Point = station.c.toLowerCase() === '#97c616'
        && Math.hypot(station.x - changiT5Tel.x, station.y - changiT5Tel.y) < 9;
      return !isObsoleteT5Point;
    })
    .map((station) => ({ ...station, type: 'MRT', interchange: station.i === 1 })),
  ...OFFICIAL_RAIL_STATIONS.filter((station) => station.type === 'LRT'),
];

// Several source layers contain their own copy of an interchange station.
// Collapse nearby copies into one marker and always prefer an interchange
// oval over an ordinary circle.
const DISPLAY_STATIONS = RAW_DISPLAY_STATIONS.filter((station, index, stations) => {
  const colocated = stations
    .map((candidate, candidateIndex) => ({
      candidate,
      candidateIndex,
      distance: Math.hypot(candidate.x - station.x, candidate.y - station.y),
    }))
    .filter(({ distance }) => distance <= 5);
  const interchange = colocated.find(({ candidate }) => candidate.interchange);
  if (interchange) return station.interchange && index === interchange.candidateIndex;
  return index === colocated[0]?.candidateIndex;
});

/**
 * The stylised Singapore map used across deck pages: light-grey island
 * silhouette with coloured MRT lines and station dots, mirroring the
 * reference deck. Rendered as a <g> so pages can layer dots/leader lines
 * in the same SVG canvas coordinate space.
 */
export const DeckMap: React.FC<DeckMapProps> = ({
  rect,
  islandFill = '#e8eaee',
  showStations = true,
  opacity = 1,
  mainlandOnly = false,
}) => {
  const s = rect.w / MAP_VIEWBOX_WIDTH;
  const mapId = useId().replace(/:/g, '');
  const landMaskId = `pipeline-land-${mapId}-mask`;
  const visibleLandPaths = ISLAND_PATHS.filter((_, index) => !mainlandOnly || MAINLAND_PATH_INDEXES.has(index));
  return (
    <g transform={`translate(${rect.x} ${rect.y}) scale(${s})`} opacity={opacity}>
      <defs>
        <mask id={landMaskId} maskUnits="userSpaceOnUse" x={0} y={0} width={MAP_VIEWBOX_WIDTH} height={MAP_VIEWBOX_HEIGHT}>
          <rect width={MAP_VIEWBOX_WIDTH} height={MAP_VIEWBOX_HEIGHT} fill="#000000" />
          {visibleLandPaths.map((d, index) => <path key={`mask-land-${index}`} d={d} fill="#ffffff" />)}
          {WATERBODY_PATHS.map((d, index) => <path key={`mask-water-${index}`} d={d} fill="#000000" />)}
        </mask>
        {MRT_LINES.map((line) => {
          const planned = PLANNED_ROUTES[line.id];
          const routes = planned?.length ? planned : CURRENT_MRT_ROUTES[line.id];
          return routes?.length ? (
            <mask key={line.id} id={`pipeline-route-${mapId}-${line.id}`} maskUnits="userSpaceOnUse" x={0} y={0} width={MAP_VIEWBOX_WIDTH} height={MAP_VIEWBOX_HEIGHT}>
              <rect width={MAP_VIEWBOX_WIDTH} height={MAP_VIEWBOX_HEIGHT} fill="#000000" />
              {routes.map((points, index) => (
                <polyline
                  key={index}
                  points={points.map(([x, y]) => `${x},${y}`).join(' ')}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={44}
                  strokeLinecap="butt"
                  strokeLinejoin="round"
                />
              ))}
            </mask>
          ) : null;
        })}
      </defs>
      <rect
        width={MAP_VIEWBOX_WIDTH}
        height={MAP_VIEWBOX_HEIGHT}
        fill={islandFill}
        mask={`url(#${landMaskId})`}
      />
      {MRT_LINES.flatMap((line) => {
        // Operational lines use the detailed track geometry sourced from the
        // route relations. Future JRL/CRL lines retain their indicative URA
        // planning geometry instead of being reduced to straight segments
        // between station points.
        const paths = CURRENT_MRT_PATHS[line.id] || [line.path];
        return paths.map((d, index) => (
          <path
            key={`${line.id}-${index}`}
            d={d}
            fill="none"
            stroke={line.color}
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            mask={(PLANNED_ROUTES[line.id]?.length || CURRENT_MRT_ROUTES[line.id]?.length)
              ? `url(#pipeline-route-${mapId}-${line.id})`
              : undefined}
          />
        ));
      })}
      {/* These extensions begin at former route terminals and therefore must
          sit outside the terminal-trimming masks used above. */}
      <path d={TEL5_EXTENSION_PATH} fill="none" stroke="#9D5B25" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
      <path d={DTL3_EXTENSION_PATH} fill="none" stroke="#005ec4" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
      <path d={CCL6_PATH} fill="none" stroke="#fa9e0d" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
      <path d={CRL3_PATH} fill="none" stroke="#97C616" strokeWidth={2.4} strokeDasharray="6 4" strokeLinecap="butt" strokeLinejoin="round" />
      <path d={TEL_CHANGI_EXTENSION_PATH} fill="none" stroke="#9D5B25" strokeWidth={2.4} strokeDasharray="6 4" strokeLinecap="butt" strokeLinejoin="round" />
      <path
        d={DTL2_EXTENSION_PATH}
        fill="none"
        stroke="#005ec4"
        strokeWidth={2.4}
        strokeDasharray="6 4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {LRT_PATHS.map((d, index) => (
        <path
          key={`lrt-${index}`}
          d={d}
          fill="none"
          stroke="#7c8491"
          strokeWidth={1.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {showStations &&
        DISPLAY_STATIONS.map((st, i) =>
          st.interchange ? (
            <rect
              key={i}
              x={st.x - 5}
              y={st.y - 3.2}
              width={10}
              height={6.4}
              rx={3.2}
              fill="#ffffff"
              stroke="#5b6470"
              strokeWidth={1.2}
            />
          ) : (
            <circle
              key={i}
              cx={st.x}
              cy={st.y}
              r={st.type === 'LRT' ? 1.8 : 2.2}
              fill="#ffffff"
              stroke={st.type === 'LRT' ? '#7c8491' : '#5b6470'}
              strokeWidth={1.1}
            />
          ),
        )}
    </g>
  );
};

interface MapLegendProps {
  x: number;
  y: number;
  w?: number;
  includeDeckKey?: boolean;
  launchingSoonColor?: string;
  launchingSoonText?: string;
}

/** MRT line legend box (bottom-right of map pages, as in the deck). */
export const MapLegend: React.FC<MapLegendProps> = ({
  x, y, w = 300, includeDeckKey = false, launchingSoonColor = '#111111', launchingSoonText = ': Launching Soon!',
}) => {
  // Use a wider internal view so long labels gain real room instead of
  // enlarging at the same rate as the containing box.
  const baseW = 340;
  const scale = w / baseW;
  const rowH = 17;
  const colW = baseW / 2 - 10;
  const legendLines = [...MRT_LINES, { id: 'lrt', label: 'LRT Line', color: '#7c8491' }];
  const rows = Math.ceil(legendLines.length / 2) + 1; // + interchange row
  const h = (rows + (includeDeckKey ? 2 : 0)) * rowH + 30;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <rect width={baseW} height={h} rx={3} fill="#ffffff" stroke="#c7ccd4" />
      <text x={12} y={19} fontSize={11} fontWeight={700} fill="#1f2a44" letterSpacing={0.5}>
        LEGEND
      </text>
      {legendLines.map((line, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const lx = 12 + col * (colW + 10);
        const ly = 34 + row * rowH;
        return (
          <g key={line.id}>
            <rect x={lx} y={ly} width={26} height={4.5} rx={2} fill={line.color} />
            <text x={lx + 32} y={ly + 5.5} fontSize={7.6} fontWeight={600} fill="#1f2a44">
              {line.label.toUpperCase()}
            </text>
          </g>
        );
      })}
      {(() => {
        const i = legendLines.length;
        const col = i % 2;
        const row = Math.floor(i / 2);
        const lx = 12 + col * (colW + 10);
        const ly = 34 + row * rowH;
        return (
          <g>
            <rect x={lx} y={ly - 1} width={26} height={7} rx={3.5} fill="#ffffff" stroke="#5b6470" strokeWidth={1.2} />
            <text x={lx + 32} y={ly + 5.5} fontSize={7.6} fontWeight={600} fill="#1f2a44">
              INTERCHANGE
            </text>
          </g>
        );
      })()}
      {includeDeckKey && (
        <>
          <text x={12} y={30 + rows * rowH + 5.5} fontSize={9.5} fontWeight={800} fill="#1f2a44">
            * = Project Name
          </text>
          <LaunchingSoonKey
            x={12}
            y={30 + (rows + 1) * rowH - 1}
            color={launchingSoonColor}
            label={launchingSoonText}
          />
        </>
      )}
    </g>
  );
};

/** Height of a standalone project-name key rendered above the legend table. */
export const PROJECT_NAME_KEY_HEIGHT = 28;

/**
 * Renders the project-name key separately from the legend table.  Downstream
 * pages use this as the only deck key; the launching-soon key remains an
 * Overview-only affordance.
 */
export const ProjectNameKey: React.FC<{ width: number; scale?: number }> = ({ width, scale = 1 }) => {
  const baseW = 340;
  const availableBaseW = Math.max(80, width / scale);
  return (
    <g transform={`scale(${scale})`}>
      <text
        x={baseW}
        y={18}
        textAnchor="end"
        fontSize={16}
        fontWeight={800}
        fill="#ffffff"
        lengthAdjust="spacingAndGlyphs"
        // Keep the key prominent while occupying roughly half of the legend
        // width, leaving the table itself visually dominant.
        textLength={Math.min(baseW / 2, availableBaseW / 2)}
      >
        * = Project Name
      </text>
    </g>
  );
};

export function mapLegendHeight(width: number, includeDeckKey = false, projectNameKeyAbove = false): number {
  const rows = Math.ceil((MRT_LINES.length + 1) / 2) + 1 + (includeDeckKey ? 2 : 0);
  const keyAbove = projectNameKeyAbove ? PROJECT_NAME_KEY_HEIGHT : 0;
  return (rows * 17 + 30 + keyAbove) * (width / 340);
}

/** Reusable legend key for projects that are launching soon. */
export const LaunchingSoonKey: React.FC<{
  x: number;
  y: number;
  color: string;
  label: string;
  scale?: number;
}> = ({ x, y, label, scale = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <rect width={26} height={7} rx={1.5} fill="none" stroke="#000000" strokeWidth={1.1} />
    <text x={34} y={6.5} fontSize={8.5} fontWeight={700} fill="#1f2a44">
      {label}
    </text>
  </g>
);

interface AdjustableMapLegendProps {
  x: number;
  y: number;
  w: number;
  editable?: boolean;
  onChange?: (x: number, y: number, width: number) => void;
  includeDeckKey?: boolean;
  /** Render only the project-name key above the table (for downstream pages). */
  projectNameKeyAbove?: boolean;
  launchingSoonColor?: string;
  launchingSoonText?: string;
  onLaunchingSoonColorChange?: (color?: string) => void;
  onLaunchingSoonTextChange?: (text?: string) => void;
}

/** Map legend that can be repositioned and resized while editing a slide. */
export const AdjustableMapLegend: React.FC<AdjustableMapLegendProps> = ({
  x, y, w, editable = false, onChange,
  includeDeckKey = false, projectNameKeyAbove = false,
  launchingSoonColor = '#111111', launchingSoonText = ': Launching Soon!',
  onLaunchingSoonColorChange, onLaunchingSoonTextChange,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [contextMenu, setContextMenu] = React.useState<{ top: number; left: number } | null>(null);
  const gesture = useRef<{
    mode: 'move' | 'resize'; pointerX: number; pointerY: number;
    x: number; y: number; width: number; scale: number;
  } | null>(null);
  const h = mapLegendHeight(w, includeDeckKey, projectNameKeyAbove);
  const legendScale = w / 340;
  const keyAboveHeight = projectNameKeyAbove ? PROJECT_NAME_KEY_HEIGHT * legendScale : 0;
  const canCustomizeLaunchingSoon = Boolean(onLaunchingSoonColorChange || onLaunchingSoonTextChange);

  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute', left: x, top: y, width: w, height: h,
        // Keep the legend behind regional tables when their areas overlap.
        // Tables and their editing handles are intentionally above this layer.
        zIndex: 1, cursor: editable ? 'move' : 'default', touchAction: 'none',
      }}
      onPointerDown={(event) => {
        if (!editable) return;
        const canvas = ref.current?.closest('[data-pipeline-page]') as HTMLElement | null;
        if (!canvas) return;
        gesture.current = {
          mode: (event.target as HTMLElement).closest('[data-legend-resize]') ? 'resize' : 'move',
          pointerX: event.clientX, pointerY: event.clientY, x, y, width: w,
          scale: canvas.getBoundingClientRect().width / 1600,
        };
        ref.current?.setPointerCapture(event.pointerId);
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        if (!gesture.current) return;
        const current = gesture.current;
        const dx = (event.clientX - current.pointerX) / current.scale;
        const dy = (event.clientY - current.pointerY) / current.scale;
        if (current.mode === 'resize') {
          const width = Math.min(Math.max(current.width + dx, 180), 1600 - current.x);
          onChange?.(current.x, current.y, width);
        } else {
          const nextX = Math.min(Math.max(current.x + dx, 0), 1600 - current.width);
          const movingHeight = mapLegendHeight(current.width, includeDeckKey, projectNameKeyAbove);
          const nextY = Math.min(Math.max(current.y + dy, 0), 900 - movingHeight);
          onChange?.(nextX, nextY, current.width);
        }
      }}
      onPointerUp={() => { gesture.current = null; }}
      onPointerCancel={() => { gesture.current = null; }}
      onContextMenu={(event) => {
        if (!editable || !includeDeckKey || !canCustomizeLaunchingSoon) return;
        event.preventDefault();
        event.stopPropagation();
        gesture.current = null;
        setContextMenu({ top: event.clientY, left: event.clientX });
      }}
    >
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', overflow: 'visible' }}>
        {projectNameKeyAbove && <ProjectNameKey width={w} scale={legendScale} />}
        <MapLegend
          x={0}
          y={keyAboveHeight}
          w={w}
          includeDeckKey={includeDeckKey}
          launchingSoonColor={launchingSoonColor}
          launchingSoonText={launchingSoonText}
        />
      </svg>
      {editable && (
        <Box
          data-legend-resize
          title="Drag to resize legend"
          sx={{
            position: 'absolute', right: -5, top: h - 9, width: 14, height: 14,
            bgcolor: '#ffffff', border: '2px solid #1f2a44', borderRadius: '2px',
            cursor: 'nwse-resize', boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
          }}
        />
      )}
      {editable && includeDeckKey && canCustomizeLaunchingSoon && (
        <Menu
          open={contextMenu != null}
          onClose={() => setContextMenu(null)}
          onPointerDown={(event) => event.stopPropagation()}
          anchorReference="anchorPosition"
          anchorPosition={contextMenu ?? undefined}
          MenuListProps={{ dense: true, 'aria-label': 'Legend options' }}
        >
          <MenuItem onClick={(event) => event.stopPropagation()} sx={{ gap: 1.5 }}>
            <Box
              component="input"
              type="color"
              aria-label="Choose launching soon colour"
              value={launchingSoonColor}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => onLaunchingSoonColorChange?.(event.target.value)}
              sx={{ width: 34, height: 26, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
            />
            Launching soon colour
          </MenuItem>
          <MenuItem onClick={(event) => event.stopPropagation()} sx={{ display: 'block' }}>
            <TextField
              size="small"
              fullWidth
              label="Launching soon label"
              value={launchingSoonText}
              onChange={(event) => onLaunchingSoonTextChange?.(event.target.value)}
              onClick={(event) => event.stopPropagation()}
            />
          </MenuItem>
          <MenuItem onClick={() => {
            onLaunchingSoonColorChange?.(undefined);
            onLaunchingSoonTextChange?.(undefined);
            setContextMenu(null);
          }}>
            Reset launching soon key
          </MenuItem>
        </Menu>
      )}
    </Box>
  );
};

/** Independently movable pricing/source caption used on the overview page. */
const CAPTION_BASE_WIDTH = 440;
const CAPTION_BASE_HEIGHT = 21;
const CAPTION_MIN_WIDTH = 280;
const CAPTION_MAX_WIDTH = 1200;

export const DeckLegendCaption: React.FC<{
  x: number;
  y: number;
  asAtDate: string;
  width?: number;
  editable?: boolean;
  onChange?: (x: number, y: number, width: number) => void;
}> = ({ x, y, asAtDate, width = CAPTION_BASE_WIDTH, editable = false, onChange }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const captionWidth = Math.min(CAPTION_MAX_WIDTH, Math.max(CAPTION_MIN_WIDTH, width));
  const captionScale = captionWidth / CAPTION_BASE_WIDTH;
  const captionHeight = CAPTION_BASE_HEIGHT * captionScale;
  const drag = useRef<{
    mode: 'move' | 'resize'; px: number; py: number; x: number; y: number;
    width: number; scale: number;
  } | null>(null);
  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute', left: x, top: y, width: captionWidth, height: captionHeight,
        color: '#ffffff', lineHeight: 1.15, zIndex: 1, cursor: editable ? 'move' : 'default',
        touchAction: 'none', overflow: 'visible', whiteSpace: 'nowrap',
      }}
      onPointerDown={(event) => {
        if (!editable) return;
        const canvas = ref.current?.closest('[data-pipeline-page]') as HTMLElement | null;
        if (!canvas) return;
        const mode = (event.target as HTMLElement).closest('[data-caption-resize]') ? 'resize' : 'move';
        drag.current = {
          mode, px: event.clientX, py: event.clientY, x, y, width: captionWidth,
          scale: canvas.getBoundingClientRect().width / 1600,
        };
        ref.current?.setPointerCapture(event.pointerId);
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        if (!drag.current) return;
        const current = drag.current;
        const dx = (event.clientX - current.px) / current.scale;
        const dy = (event.clientY - current.py) / current.scale;
        if (current.mode === 'resize') {
          const nextWidth = Math.min(
            CAPTION_MAX_WIDTH,
            Math.max(CAPTION_MIN_WIDTH, Math.min(current.width + dx, 1600 - current.x)),
          );
          onChange?.(current.x, current.y, nextWidth);
          return;
        }
        const nextWidth = current.width;
        const nextHeight = CAPTION_BASE_HEIGHT * nextWidth / CAPTION_BASE_WIDTH;
        onChange?.(
          Math.min(Math.max(current.x + dx, 0), 1600 - nextWidth),
          Math.min(Math.max(current.y + dy, 0), 900 - nextHeight),
          nextWidth,
        );
      }}
      onPointerUp={(event) => {
        if (ref.current?.hasPointerCapture(event.pointerId)) ref.current.releasePointerCapture(event.pointerId);
        drag.current = null;
      }}
      onPointerCancel={(event) => {
        if (ref.current?.hasPointerCapture(event.pointerId)) ref.current.releasePointerCapture(event.pointerId);
        drag.current = null;
      }}
    >
      <Box sx={{ fontSize: 8 * captionScale }}>Estimated Average Launch Prices (S$)</Box>
      <Box sx={{ fontSize: 7.5 * captionScale }}>
        Source: Newspaper articles, in-house estimates as of {asAtDate}
        <br />
        (Subject to change without notice)
      </Box>
      {editable && (
        <Box
          data-caption-resize
          sx={{
            position: 'absolute', right: -6, bottom: -6, width: 10, height: 10,
            border: '1px solid rgba(255,255,255,0.95)', backgroundColor: 'rgba(13,44,92,0.75)',
            cursor: 'nwse-resize', boxSizing: 'border-box',
          }}
        />
      )}
    </Box>
  );
};
